import { useState, useMemo, useCallback } from 'react'
import { POI } from '@cya-routes/shared'
import { POI_CATEGORIES } from '../../../data/poiTypes'

interface UsePOIManagementProps {
  pois: POI[]
}

interface UsePOIManagementReturn {
  selectedPOI: POI | null
  setSelectedPOI: (poi: POI | null) => void
  showPOIModal: boolean
  setShowPOIModal: (show: boolean) => void
  showPOIs: boolean
  setShowPOIs: (show: boolean) => void
  hiddenPOICategories: Set<string>
  setHiddenPOICategories: (categories: Set<string>) => void
  visiblePOIs: POI[]
  handlePOISelect: (poi: POI) => void
}

/**
 * Custom hook that manages POI state and interactions
 * Handles POI selection, visibility, and category filtering
 */
export function usePOIManagement({
  pois
}: UsePOIManagementProps): UsePOIManagementReturn {
  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null)
  const [showPOIModal, setShowPOIModal] = useState(false)
  const [showPOIs, setShowPOIs] = useState(true)
  const [hiddenPOICategories, setHiddenPOICategories] = useState<Set<string>>(new Set())
  
  // Memoize visible POIs to prevent re-filtering on every render
  const visiblePOIs = useMemo(() => {
    if (!showPOIs) {
      return []
    }
    
    return pois.filter(poi => {
      const poiCategory = POI_CATEGORIES.find(cat => 
        cat.types.some(type => type.id === poi.type)
      )
      return poiCategory && !hiddenPOICategories.has(poiCategory.id)
    })
  }, [pois, showPOIs, hiddenPOICategories])
  
  // Handle POI selection
  const handlePOISelect = useCallback((poi: POI) => {
    setSelectedPOI(poi)
    setShowPOIModal(true)
  }, [])
  
  return {
    selectedPOI,
    setSelectedPOI,
    showPOIModal,
    setShowPOIModal,
    showPOIs,
    setShowPOIs,
    hiddenPOICategories,
    setHiddenPOICategories,
    visiblePOIs,
    handlePOISelect
  }
}